// Simple-text fields (SQL queries, credentials JSON) hold the value verbatim.
// One paragraph per line, and getText joins them back with "\n", so a value
// survives the editor byte for byte and never goes through markdown.
export const PLAIN_TEXT_BLOCK_SEPARATOR = "\n";

interface PlainTextNode {
  type: "text";
  text: string;
}

interface PlainTextParagraph {
  type: "paragraph";
  content?: PlainTextNode[];
}

export interface PlainTextDoc {
  type: "doc";
  content: PlainTextParagraph[];
}

export function plainTextToDoc(text: string): PlainTextDoc {
  if (!text) return { type: "doc", content: [] };
  return {
    type: "doc",
    content: text
      .split("\n")
      .map((line) =>
        line.length > 0
          ? { type: "paragraph", content: [{ type: "text", text: line }] }
          : { type: "paragraph" }
      ),
  };
}

// The node types the simple-text editor registers. It has no marks.
const PLAIN_TEXT_NODE_TYPES = new Set(["doc", "paragraph", "text", "mention"]);

interface StoredNode {
  type?: unknown;
  marks?: unknown;
  content?: unknown;
}

/** True when a stored TipTap document loads in the simple-text schema.
 *  Documents saved by the markdown editor can carry a code mark (from
 *  backticks), a list, a heading or a hardBreak. TipTap cannot load those
 *  into the simple-text schema and mounts an empty field instead. */
export function fitsPlainTextSchema(doc: unknown): boolean {
  if (!doc || typeof doc !== "object") return false;
  const node = doc as StoredNode;
  if (typeof node.type !== "string" || !PLAIN_TEXT_NODE_TYPES.has(node.type)) {
    return false;
  }
  if (Array.isArray(node.marks) && node.marks.length > 0) return false;
  if (node.content == null) return true;
  return (
    Array.isArray(node.content) &&
    node.content.every((child) => fitsPlainTextSchema(child))
  );
}
